// ─── Game Loop ────────────────────────────────────────────────────────────────

import { FRAME_TIME, TARGET_FPS } from "./constants";

export type UpdateFn = (dt: number) => void;
export type RenderFn = () => void;

/**
 * GameLoop manages the requestAnimationFrame loop.
 * Targets 60 FPS with delta-time tracking.
 */
export class GameLoop {
  private animFrameId: number = 0;
  private running: boolean = false;
  private lastTime: number = 0;
  private accumulator: number = 0;
  private frameCount: number = 0;
  private fpsTimer: number = 0;
  private currentFps: number = 0;

  private updateFn: UpdateFn;
  private renderFn: RenderFn;

  constructor(updateFn: UpdateFn, renderFn: RenderFn) {
    this.updateFn = updateFn;
    this.renderFn = renderFn;
  }

  /**
   * Start the game loop.
   */
  start(): void {
    if (this.running) return;

    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.fpsTimer = 0;
    this.frameCount = 0;

    const loop = (now: number) => {
      if (!this.running) return;

      const elapsed = now - this.lastTime;
      this.lastTime = now;

      // Cap elapsed to prevent spiral of death after tab switch
      const cappedElapsed = Math.min(elapsed, 100);

      this.accumulator += cappedElapsed;

      // Fixed timestep updates
      while (this.accumulator >= FRAME_TIME) {
        this.updateFn(FRAME_TIME);
        this.accumulator -= FRAME_TIME;
      }

      // Render once per frame
      this.renderFn();

      // FPS tracking
      this.frameCount++;
      this.fpsTimer += elapsed;
      if (this.fpsTimer >= 1000) {
        this.currentFps = this.frameCount;
        this.frameCount = 0;
        this.fpsTimer -= 1000;
      }

      this.animFrameId = requestAnimationFrame(loop);
    };

    this.animFrameId = requestAnimationFrame(loop);
  }

  /**
   * Stop the game loop.
   */
  stop(): void {
    this.running = false;
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = 0;
    }
  }

  /**
   * Whether the loop is currently running.
   */
  get isRunning(): boolean {
    return this.running;
  }

  /**
   * Current measured FPS.
   */
  get fps(): number {
    return this.currentFps;
  }

  /**
   * Total frames rendered (wraps at max safe int — essentially infinite).
   */
  get totalFrames(): number {
    return this.frameCount;
  }
}
